import { useSearch } from '@faststore/sdk'
import classNames from 'classnames'
import { useModalSheet } from 'src/components/contexts/ModalSheetContext'
import { CheckSelectedIcon } from 'src/components/Icons/CheckSelectedIcon'
import { OptionsMap as sortOptions } from 'src/components/search/SortV2/Sort'
import type { MouseEvent } from 'react'
import { useEffect, useState } from 'react'
import { ChevronDown } from 'src/components/Icons/ChevronDown'
import { useMobile } from 'src/hooks/useMobile'

const OptionsMap = {
  score_desc: 'Relevância',
  orders_desc: 'Mais vendidos',
  price_asc: 'Mais barato',
  price_desc: 'Mais caro',
  discount_desc: 'Maior desconto',
}

type SortOption = keyof typeof sortOptions

const keys = Object.keys(OptionsMap) as Array<keyof typeof OptionsMap>

const SortModalContent = ({
  sort,
  setSort,
  closeModal,
}: {
  sort: SortOption
  setSort: (sort: SortOption) => void
  closeModal: () => void
}) => {
  const handleSortClick = (key: SortOption) => {
    setSort(key)
    closeModal()
  }

  return (
    <div className="flex flex-col gap-5 items-start px-7 pb-6">
      {Object.entries(sortOptions).map(([key, value]) => {
        const isSelected = sort === key

        return (
          <button
            onClick={() => handleSortClick(key as SortOption)}
            data-testid={`gallery-sort-option-${key}`}
            className={classNames(
              'flex items-center gap-[6px] font-semibold text-restructure-primary',
              {
                '!text-primaryBlue': isSelected,
              }
            )}
            key={key}
          >
            {value} {isSelected && <CheckSelectedIcon />}
          </button>
        )
      })}
    </div>
  )
}

function SortV3() {
  const {
    setSort,
    state: { sort },
  } = useSearch()

  const { openModal, closeModal } = useModalSheet()
  const { isMobile } = useMobile()

  const [isOpen, setIsOpen] = useState(false)

  useEffect(() => {
    const handler = () => setIsOpen(false)

    window.addEventListener('click', handler)

    return () => {
      window.removeEventListener('click', handler)
    }
  }, [])

  const handleOpenOptions = (e: MouseEvent) => {
    e.stopPropagation()

    if (isMobile) {
      return handleOpenModal()
    }

    return setIsOpen((current) => !current)
  }

  const handleOpenModal = () => {
    openModal({
      position: 'bottom',
      title: 'Ordenar por',
      content: (
        <SortModalContent
          sort={sort as keyof typeof sortOptions}
          setSort={setSort}
          closeModal={closeModal}
        />
      ),
    })
  }

  return (
    <div className="relative">
      <button
        data-testid="gallery-sort-button"
        className="flex items-center text-restructure-primary font-medium text-sm gap-[2px]"
        onClick={handleOpenOptions}
      >
        Ordenar por
        <ChevronDown color="#101010" width={16} height={16} />
      </button>
      {isOpen && (
        <ul className="absolute bg-white px-2 top-6 right-0 h-fit z-50 shadow-[0px 24px 24px rgba(0, 83, 125, 0.1)] border border-gray rounded min-w-[157px]">
          {keys.map((key, index) => (
            <li key={key}>
              <label
                onClick={() => {
                  setSort(key)
                  setIsOpen(false)
                }}
                data-testid={`gallery-sort-option-${key}`}
                aria-hidden="true"
                className={`relative flex items-center justify-between text-sm font-inter py-[12px] px-2 ${
                  index > 0 ? 'border-t border-gray' : ''
                } ${
                  key === sort ? 'text-restructure-action' : 'text-neutral10'
                } `}
              >
                <input
                  type="radio"
                  name="sort"
                  value={key}
                  className="w-0 h-0 invisible absolute top-0 left-0"
                />
                <p>{OptionsMap[key]}</p>
                <span className="w-[20px]">
                  {key === sort && (
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="18"
                      height="13"
                      viewBox="0 0 18 13"
                      fill="none"
                    >
                      <path
                        d="M6.99999 10.172L16.192 0.979004L17.607 2.393L6.99999 13L0.635986 6.636L2.04999 5.222L6.99999 10.172Z"
                        fill="#3643ba"
                      />
                    </svg>
                  )}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default SortV3
